"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.InlineRunSchema = exports.INLINE_MARKS = void 0;
exports.runsToText = runsToText;
exports.runsMatchText = runsMatchText;
exports.plainRuns = plainRuns;
const zod_1 = require("zod");
/**
 * INLINE FORMATTING, AS RUNS.
 *
 * A block's `text` stays required and authoritative — it is what
 * `requiredMergeFields` scans and what `diffBlocks` compares, so a formatting
 * change can never be mistaken for a wording change in the publish gate.
 * `runs` is OPTIONAL and presentation-only: a renderer that ignores it still
 * produces a correct document, which is what makes this additive to the 76
 * documents already written.
 *
 * The marks are a CLOSED SET. Both renderers implement exactly these four, and
 * the editor's Quill→runs conversion maps onto exactly these four, so nothing an
 * author pastes can introduce formatting the PDF cannot draw.
 */
exports.INLINE_MARKS = ['bold', 'italic', 'underline', 'link', 'ref'];
exports.InlineRunSchema = zod_1.z.object({
    /** May be empty — Quill emits empty runs mid-edit and normalising here would fight it. */
    text: zod_1.z.string(),
    marks: zod_1.z.array(zod_1.z.enum(exports.INLINE_MARKS)).optional(),
    /**
     * Validated as a URL, not free text: this value reaches an `href` in a
     * document a customer signs, and `javascript:` must never survive to it.
     *
     * `z.string().url()` alone is not enough — the WHATWG URL constructor it
     * delegates to accepts ANY scheme, `javascript:` included. The protocol
     * check below is what actually excludes it.
     */
    href: zod_1.z.string().url().refine((v) => {
        try {
            return ['http:', 'https:'].includes(new URL(v).protocol);
        }
        catch {
            return false;
        }
    }, { message: 'href must be an http(s) URL' }).optional(),
    /**
     * For a run marked `ref`: the id of the heading block this points at.
     *
     * The reference RENDERS as that heading's computed number, so inserting a
     * clause renumbers the heading and every reference to it in one move. The
     * run's own `text` is kept as the fallback for an unresolvable target — a
     * reference whose heading was deleted must still render its last known words
     * rather than vanish from a contract.
     */
    refBlockId: zod_1.z.string().min(1).optional(),
}).refine((r) => !r.marks?.includes('ref') || r.refBlockId !== undefined, {
    message: 'a run marked `ref` must carry `refBlockId`',
    path: ['refBlockId'],
}).refine((r) => r.refBlockId === undefined || !!r.marks?.includes('ref'), {
    message: 'a run carrying `refBlockId` must be marked `ref`',
    path: ['marks'],
});
function runsToText(runs) {
    return runs.map((r) => r.text).join('');
}
/**
 * THE INVARIANT. When runs are present they must reproduce `text` exactly —
 * byte for byte, whitespace included.
 *
 * Absent runs are always valid: that is the migration-free case, and every one
 * of the 76 seeded documents is in it.
 *
 * The comparison is deliberately exact rather than trimmed or normalised. A
 * lenient check would let a formatting edit quietly alter the wording of a
 * clause while the diff — which reads `text` — showed nothing.
 */
function runsMatchText(runs, text) {
    if (runs === undefined)
        return true;
    return runsToText(runs) === text;
}
/** The unformatted equivalent of `text`, for seeding an editor from a plain block. */
function plainRuns(text) {
    return text === '' ? [] : [{ text }];
}
//# sourceMappingURL=inlineRuns.js.map